import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";

// ============================================
// Constants
// ============================================

export const SIDEBAR_WIDTH = "280px";
export const SIDEBAR_COLLAPSED_WIDTH = "72px";
// The expanded sidebar can be dragged wider or narrower. Below the minimum the
// labels truncate to nothing useful; above the maximum it eats the register.
export const SIDEBAR_DEFAULT_WIDTH_PX = parseInt(SIDEBAR_WIDTH, 10);
export const SIDEBAR_MIN_WIDTH_PX = 200;
export const SIDEBAR_MAX_WIDTH_PX = 480;
const COLLAPSE_STORAGE_KEY = "greenback_sidebar_collapsed";
const WIDTH_STORAGE_KEY = "greenback_sidebar_width";
const MOBILE_BREAKPOINT = 768;

// ============================================
// Types
// ============================================

interface SidebarContextValue {
    isCollapsed: boolean;
    toggleSidebar: () => void;
    setSidebarCollapsed: (collapsed: boolean) => void;
    sidebarWidth: string;

    // Width of the expanded sidebar in px, chosen by dragging its edge
    expandedWidth: number;
    setExpandedWidth: (width: number) => void;
    resetExpandedWidth: () => void;
    // True while the edge is being dragged, so layouts can drop their width
    // transitions and follow the pointer instead of trailing behind it
    isResizing: boolean;
    setIsResizing: (resizing: boolean) => void;

    // Navigation State
    expandedItems: Record<string, boolean>;
    toggleExpandedItem: (label: string) => void;
    setExpandedItem: (label: string, expanded: boolean) => void;

    collapsedSections: Record<string, boolean>;
    toggleSection: (title: string) => void;
}

// ============================================
// Context
// ============================================

const SidebarContext = createContext<SidebarContextValue | undefined>(undefined);

// ============================================
// Helper Functions
// ============================================

const getStoredCollapseState = (): boolean => {
    try {
        const stored = localStorage.getItem(COLLAPSE_STORAGE_KEY);
        return stored ? JSON.parse(stored) : false;
    } catch (e) {
        return false;
    }
};

export const clampSidebarWidth = (width: number): number =>
    Number.isFinite(width)
        ? Math.round(Math.min(SIDEBAR_MAX_WIDTH_PX, Math.max(SIDEBAR_MIN_WIDTH_PX, width)))
        : SIDEBAR_DEFAULT_WIDTH_PX;

const getStoredWidth = (): number => {
    try {
        const stored = Number(localStorage.getItem(WIDTH_STORAGE_KEY));
        return stored ? clampSidebarWidth(stored) : SIDEBAR_DEFAULT_WIDTH_PX;
    } catch (e) {
        return SIDEBAR_DEFAULT_WIDTH_PX;
    }
};

// ============================================
// Provider Component
// ============================================

interface SidebarProviderProps {
    children: ReactNode;
}

export const SidebarProvider: React.FC<SidebarProviderProps> = ({ children }) => {
    const [isCollapsed, setIsCollapsedState] = useState<boolean>(getStoredCollapseState);
    const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});
    const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
    const [expandedWidth, setExpandedWidthState] = useState<number>(getStoredWidth);
    const [isResizing, setIsResizing] = useState(false);

    const setExpandedWidth = useCallback((width: number) => {
        const next = clampSidebarWidth(width);
        setExpandedWidthState(next);
        try {
            localStorage.setItem(WIDTH_STORAGE_KEY, String(next));
        } catch (e) {
            // ignore
        }
    }, []);

    const resetExpandedWidth = useCallback(() => {
        setExpandedWidthState(SIDEBAR_DEFAULT_WIDTH_PX);
        try {
            localStorage.removeItem(WIDTH_STORAGE_KEY);
        } catch (e) {
            // ignore
        }
    }, []);

    // Persist collapse state
    const setSidebarCollapsed = useCallback((collapsed: boolean) => {
        setIsCollapsedState(collapsed);
        try {
            localStorage.setItem(COLLAPSE_STORAGE_KEY, JSON.stringify(collapsed));
        } catch (e) {
            // ignore
        }
    }, []);

    const toggleSidebar = useCallback(() => {
        setSidebarCollapsed(!isCollapsed);
    }, [isCollapsed, setSidebarCollapsed]);

    // Navigation State Handlers
    const toggleExpandedItem = useCallback((label: string) => {
        setExpandedItems(prev => ({
            ...prev,
            [label]: !prev[label]
        }));
    }, []);

    const setExpandedItem = useCallback((label: string, expanded: boolean) => {
        setExpandedItems(prev => ({
            ...prev,
            [label]: expanded
        }));
    }, []);

    const toggleSection = useCallback((title: string) => {
        setCollapsedSections(prev => ({
            ...prev,
            [title]: !prev[title]
        }));
    }, []);

    // Auto-collapse on mobile resize (logic migrated from AppShell)
    useEffect(() => {
        const handleResize = () => {
            const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;
            // If on mobile and not already collapsed, collapse it.
            // Note: We might not want to auto-expand on desktop if user manually collapsed it.
            if (isMobile && !isCollapsed) {
                setSidebarCollapsed(true);
            }
        };

        // Run once on mount
        handleResize();

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [isCollapsed, setSidebarCollapsed]);

    const value: SidebarContextValue = {
        isCollapsed,
        toggleSidebar,
        setSidebarCollapsed,
        sidebarWidth: isCollapsed ? SIDEBAR_COLLAPSED_WIDTH : `${expandedWidth}px`,

        expandedWidth,
        setExpandedWidth,
        resetExpandedWidth,
        isResizing,
        setIsResizing,

        expandedItems,
        toggleExpandedItem,
        setExpandedItem,

        collapsedSections,
        toggleSection
    };

    return (
        <SidebarContext.Provider value={value}>
            {children}
        </SidebarContext.Provider>
    );
};

// ============================================
// Hook
// ============================================

export const useSidebar = (): SidebarContextValue => {
    const context = useContext(SidebarContext);
    if (!context) {
        throw new Error("useSidebar must be used within a SidebarProvider");
    }
    return context;
};
